// Circle-Spinner-only decor for Level 5 ("Circle Spinner Word Run"):
// five large round platforms with long pink bars sweeping around them,
// linked by narrow corridors that carry the checkpoint gates.
//
// Layout positions are precomputed by Path.buildCircleSpinner; this module
// owns the visual meshes + the bar rotation animation and exposes the bar
// query Game uses for player knock-back collisions.

import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { Path } from "./Path";

const CIRCLE_TOP = 0x4cc9ff;        // bright blue platform top
const CIRCLE_RIM = 0xffd166;        // gold rim
const PLATFORM_BASE = 0x8b5cf6;     // purple side wall
const CORRIDOR_COLOR = 0xff5fa7;    // pink corridor floor
const BAR_COLOR = 0xff2ca4;         // hot-pink spinner bar
const BAR_HUB = 0xa855f7;           // purple hub

export interface CircleSpinnerHandle {
  group: THREE.Group;
  /** Solid platform meshes — used by the standable-surface raycaster. */
  supportMeshes: THREE.Object3D[];
  /** Gate s-positions, for the Spawner. */
  gatePositions: number[];
  tick: (clockMs: number) => void;
  /** Returns spinning-bar contact info for the player at (s, l), or null.
   *  Game uses this to apply the same kind of knockback as a wall hit. */
  barContact: (s: number, l: number, radius: number) => {
    normalS: number;
    normalL: number;
    penetration: number;
    barVelS: number;
    barVelL: number;
    /** True for the upper bar — Game uses this to skip the hit when the
     *  player is grounded (i.e. ducked under the upper bar by staying on
     *  the deck) and require ducking instead of jumping. */
    isUpper?: boolean;
  } | null;
}

function buildCirclePlatform(radius: number): THREE.Group {
  const g = new THREE.Group();
  // Round top
  const top = new THREE.Mesh(
    new THREE.CylinderGeometry(radius, radius, 0.4, 64),
    new THREE.MeshStandardMaterial({ color: CIRCLE_TOP, roughness: 0.45 }),
  );
  top.position.y = 0;
  top.castShadow = true;
  top.receiveShadow = true;
  g.add(top);
  // Gold rim
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(radius + 0.05, 0.16, 12, 96),
    new THREE.MeshStandardMaterial({ color: CIRCLE_RIM, roughness: 0.35, metalness: 0.2 }),
  );
  rim.rotation.x = Math.PI / 2;
  rim.position.y = 0.2;
  g.add(rim);
  // Purple base wall extending downward so the circle looks like a tall
  // pedestal floating above the void.
  const base = new THREE.Mesh(
    new THREE.CylinderGeometry(radius, radius * 1.05, 0.9, 64),
    new THREE.MeshStandardMaterial({ color: PLATFORM_BASE, roughness: 0.55 }),
  );
  base.position.y = -0.55;
  g.add(base);
  return g;
}

function buildCorridor(width: number, depth: number): THREE.Group {
  const g = new THREE.Group();
  const top = new THREE.Mesh(
    new SoftBox(width, 0.36, depth),
    new THREE.MeshStandardMaterial({ color: CORRIDOR_COLOR, roughness: 0.5 }),
  );
  top.position.y = 0;
  top.receiveShadow = true;
  top.castShadow = true;
  g.add(top);
  // Pink edge stripes for visual continuity with door-dash corridors.
  const rimMat = new THREE.MeshStandardMaterial({ color: CIRCLE_RIM, roughness: 0.4 });
  for (const sign of [-1, 1] as const) {
    const r = new THREE.Mesh(new SoftBox(0.18, 0.18, depth), rimMat);
    r.position.set(sign * (width / 2 - 0.1), 0.2, 0);
    g.add(r);
  }
  return g;
}

interface Bar {
  /** s-position of the spinner's center (= circle center). */
  s: number;
  /** Half-length of the bar (reaches across the circle). */
  halfLength: number;
  /** Bar radius (collision + visual thickness). */
  thickness: number;
  /** Angular velocity (rad/s) around the vertical axis. */
  angularVel: number;
  /** Phase at clockMs=0 — deterministic per index for MP sync. */
  phase0: number;
  /** Current rotation in radians, updated each tick. */
  angle: number;
  pivot: THREE.Group;
  /** True for the upper bar (chest-high) — player must NOT be airborne
   *  to clear it (must be on the deck). False/undefined = lower bar. */
  isUpper?: boolean;
}

export function buildCircleSpinnerCourse(path: Path): CircleSpinnerHandle {
  const cs = path.circleSpinner;
  if (!cs) throw new Error("buildCircleSpinnerCourse called on non-circle-spinner path");

  const group = new THREE.Group();
  const supportMeshes: THREE.Object3D[] = [];
  const bars: Bar[] = [];

  // Overlap each corridor into adjacent circles by this much so the gap
  // between the round platform and the rectangular corridor disappears —
  // the corridor mesh slips under the circle's rim instead of meeting it
  // at a hard seam.
  const OVERLAP = 1.2;

  // Start pad — solid corridor up to the first circle. Extend slightly into
  // the circle so the two visually connect.
  const firstCircleStart = cs.circles[0]!.center - cs.circles[0]!.halfDepth;
  if (firstCircleStart > 0) {
    const len = firstCircleStart + OVERLAP;
    const cor = buildCorridor(cs.corridorHalfWidth * 2, len);
    cor.position.set(0, 0, len / 2);
    group.add(cor);
    supportMeshes.push(...cor.children.filter((c) => c instanceof THREE.Mesh));
  }

  // Circles + bars
  for (let i = 0; i < cs.circles.length; i++) {
    const c = cs.circles[i]!;
    const platform = buildCirclePlatform(c.radius);
    platform.position.set(0, 0, c.center);
    group.add(platform);
    // Top mesh of the platform is what the raycaster needs to land on.
    supportMeshes.push(...platform.children.filter((m): m is THREE.Mesh =>
      m instanceof THREE.Mesh && m.position.y >= -0.01,
    ));

    // Spinner bar: a long capsule sweeping around the circle's center.
    // Shortened + lowered so a well-timed jump clears the bar cleanly.
    const barHalfLength = c.radius - 0.4;
    const barThickness = 0.35;
    const pivot = new THREE.Group();
    pivot.position.set(0, 0.55, c.center);
    const arm = new THREE.Mesh(
      new THREE.CapsuleGeometry(barThickness, barHalfLength * 2, 12, 24),
      new THREE.MeshStandardMaterial({
        color: BAR_COLOR,
        emissive: BAR_COLOR,
        emissiveIntensity: 0.15,
        roughness: 0.45,
      }),
    );
    arm.rotation.z = Math.PI / 2;
    arm.castShadow = true;
    pivot.add(arm);
    // Bright tips
    const tipMat = new THREE.MeshStandardMaterial({ color: 0xffd166, roughness: 0.35, metalness: 0.2 });
    for (const x of [-barHalfLength, barHalfLength]) {
      const tip = new THREE.Mesh(new THREE.SphereGeometry(barThickness * 1.25, 18, 14), tipMat);
      tip.position.x = x;
      pivot.add(tip);
    }
    // Central hub
    const hub = new THREE.Mesh(
      new THREE.CylinderGeometry(0.65, 0.7, 0.7, 24),
      new THREE.MeshStandardMaterial({ color: BAR_HUB, roughness: 0.45 }),
    );
    hub.position.y = -0.35;
    pivot.add(hub);
    group.add(pivot);
    bars.push({
      s: c.center,
      halfLength: barHalfLength,
      thickness: barThickness,
      angularVel: c.angularVel,
      phase0: (i * 1.37) % (Math.PI * 2),
      angle: 0,
      pivot,
    });

    // ── Upper bar: rotates the OPPOSITE direction, slightly above
    //    the lower bar. Forces the player to time both — jump the
    //    lower, then duck (be on the deck) for the upper. Phase
    //    offset by 90° so the two bars don't overlap initially. ──
    const upperHalfLen = c.radius - 0.7;
    const upperThickness = 0.32;
    const upperPivot = new THREE.Group();
    upperPivot.position.set(0, 2.0, c.center);
    const upperArm = new THREE.Mesh(
      new THREE.CapsuleGeometry(upperThickness, upperHalfLen * 2, 12, 24),
      new THREE.MeshStandardMaterial({
        color: 0xffb84a,
        emissive: 0xffb84a,
        emissiveIntensity: 0.15,
        roughness: 0.45,
      }),
    );
    upperArm.rotation.z = Math.PI / 2;
    upperArm.castShadow = true;
    upperPivot.add(upperArm);
    for (const x of [-upperHalfLen, upperHalfLen]) {
      const tip = new THREE.Mesh(new THREE.SphereGeometry(upperThickness * 1.25, 18, 14), tipMat);
      tip.position.x = x;
      upperPivot.add(tip);
    }
    group.add(upperPivot);
    bars.push({
      s: c.center,
      halfLength: upperHalfLen,
      thickness: upperThickness,
      angularVel: -c.angularVel * 0.85, // opposite direction, slightly different speed
      phase0: ((i * 1.37) % (Math.PI * 2)) + Math.PI / 2,
      angle: 0,
      pivot: upperPivot,
      isUpper: true,
    });
  }

  // Corridors between circles — extend by `OVERLAP` on both ends so the
  // corridor pokes under each adjacent circle's rim, no visible seam.
  for (const cor of cs.corridors) {
    const len = (cor.end - cor.start) + OVERLAP * 2;
    const center = (cor.start + cor.end) / 2;
    const corMesh = buildCorridor(cs.corridorHalfWidth * 2, len);
    corMesh.position.set(0, 0, center);
    // Render the corridor first so the round circles draw on top of the
    // overlap — the rim seam reads as one continuous platform.
    corMesh.renderOrder = -1;
    group.add(corMesh);
    supportMeshes.push(...corMesh.children.filter((c) => c instanceof THREE.Mesh));
  }

  // Finish pad — corridor from the last circle to the path end. Also extend
  // back under the last circle.
  const lastCircle = cs.circles[cs.circles.length - 1]!;
  const finishStart = lastCircle.center + lastCircle.halfDepth - OVERLAP;
  const finishLen = path.length - finishStart;
  if (finishLen > 0.5) {
    const cor = buildCorridor(cs.corridorHalfWidth * 2, finishLen);
    cor.position.set(0, 0, finishStart + finishLen / 2);
    cor.renderOrder = -1;
    group.add(cor);
    supportMeshes.push(...cor.children.filter((c) => c instanceof THREE.Mesh));
  }

  const tick = (clockMs: number) => {
    const tSec = clockMs / 1000;
    for (const bar of bars) {
      bar.angle = bar.phase0 + tSec * bar.angularVel;
      bar.pivot.rotation.y = bar.angle;
    }
  };

  /** Player-bar collision. The bar's mesh is a horizontal capsule whose long
   *  axis lies along the pivot's local +X. The pivot rotates around the world
   *  Y axis by `bar.angle`, so a point at parent-local (lx, 0, 0) sits in
   *  world at (lx·cos, 0, bar.s − lx·sin). In our (s, l) frame the bar
   *  direction is therefore (cos, −sin) in (l, s).
   *
   *  To find the closest point on the bar to the player we project the
   *  player's offset from the pivot onto that direction and clamp to the
   *  half-length, then measure the perpendicular distance.
   */
  const barContact = (s: number, l: number, radius: number) => {
    for (const bar of bars) {
      const dl = l;                       // pivot is at l=0
      const ds = s - bar.s;
      const cos = Math.cos(bar.angle);
      const sin = Math.sin(bar.angle);
      // World→bar-frame: bar's x-axis is (cos, −sin) in (l, s), perpendicular
      // is (sin, cos). Project the player into that frame.
      const localX = dl * cos - ds * sin;    // distance along the bar
      const localY = dl * sin + ds * cos;    // perpendicular distance
      const halfLen = bar.halfLength;
      const halfThick = bar.thickness;
      const clampedX = Math.max(-halfLen, Math.min(halfLen, localX));
      const dxLocal = localX - clampedX;
      const dyLocal = localY;
      const dist2 = dxLocal * dxLocal + dyLocal * dyLocal;
      const sumR = halfThick + radius;
      if (dist2 >= sumR * sumR) continue;
      const dist = Math.sqrt(Math.max(0.0001, dist2));
      // Local normal (from closest-segment-point to player center).
      const nxLocal = dist > 0.001 ? dxLocal / dist : 0;
      const nyLocal = dist > 0.001 ? dyLocal / dist : 1;
      // Bar-frame → world (l, s): apply the inverse rotation. If the forward
      // transform is (dl, ds) → (lx, ly) = (dl·cos − ds·sin, dl·sin + ds·cos),
      // the inverse is (lx, ly) → (lx·cos + ly·sin, −lx·sin + ly·cos).
      const normalL = nxLocal * cos + nyLocal * sin;
      const normalS = -nxLocal * sin + nyLocal * cos;
      // Surface velocity from rotation around Y at point (dl, ds):
      //   vel_x = ω·ds, vel_z = −ω·dl   →   (vel_l, vel_s) = (ω·ds, −ω·dl)
      const barVelL = bar.angularVel * ds;
      const barVelS = -bar.angularVel * dl;
      return {
        normalS,
        normalL,
        penetration: sumR - dist,
        barVelS,
        barVelL,
        isUpper: bar.isUpper,
      };
    }
    return null;
  };

  return { group, supportMeshes, gatePositions: cs.gates, tick, barContact };
}
